import { isString } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import {
  alpha,
  Theme,
  experimentalStyled as styled,
} from '@mui/material/styles';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { SxProps } from '@mui/system';
import { getBase64 } from 'src/utils/base64';
import {
  EntityType,
  Exact,
  InputMutationData_FileReqDto,
} from 'src/_types/serverTypes';
import {
  UploadMutation,
  useUploadMutation,
} from 'src/graphql/cms/file/mutations/upload.generated';
import AddImageIcon from 'src/icons/AddImage';
import { fData } from 'src/utils/formatNumber';
import { UPLOAD_ERRORS } from 'src/constants';

const RootStyle = styled('div')(({ theme }) => ({
  width: 144,
  height: 144,
  padding: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
}));

const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9,
    },
  },
});

const PlaceholderStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background2.neutral,
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': { opacity: 0.72 },
}));

interface CustomFile extends File {
  path?: string;
  preview?: string;
}

interface SmallUploadProps extends DropzoneOptions {
  id?: string;
  circle?: boolean;
  error?: boolean;
  imageCaption?: string;
  file: CustomFile | string | null;
  caption?: ReactNode;
  noMargin?: boolean;
  showError?: boolean;
  onSettled?: (
    ok: boolean,
    value: { name?: string; size?: number; type?: string; url?: string },
    { message: string }
  ) => void;
  onUploadStart?: () => void;
  onError?: (error: string) => void;
  sx?: SxProps<Theme>;
  type?: 'img' | 'video';
  entity: EntityType;
  removePreview?: boolean;
}

export default function SmallUpload({
  id,
  sx,
  file,
  type = 'img',
  error,
  entity,
  circle,
  caption,
  noMargin,
  showError,
  imageCaption,
  removePreview,
  onError,
  onSettled,
  onUploadStart,
  ...other
}: SmallUploadProps) {
  const [localFile, setLocalFile] = useState<CustomFile | string | null>(null);
  const { mutate: upload, isLoading } = useUploadMutation();

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: any) => {
    onUploadStart?.();
    if (acceptedFiles.length > 0) {
      acceptedFiles[0].preview = URL.createObjectURL(acceptedFiles[0]);
      setLocalFile(acceptedFiles[0]);
      const base64 = await getBase64(acceptedFiles[0]);

      const doSuccess = (
        data: UploadMutation,
        variables: Exact<{
          data?: InputMutationData_FileReqDto;
        }>
      ) => {
        if (data?.fileMutations?.upload?.isSuccess) {
          const { name, type: t, size } = variables.data.dto;
          const { url } = data.fileMutations.upload.listDto.items[0];

          onSettled(true, { url, name, type: t, size }, { message: '' });
          if (removePreview) setLocalFile(null);
        }
      };

      const doError = (err: any) => {
        console.log(err.message);
        onSettled(
          false,
          { url: '', name: '', type: '', size: 0 },
          { message: err.message }
        );
      };

      upload(
        {
          data: {
            dto: {
              binary: base64.split(',')[1],
              name: acceptedFiles[0].name,
              size: acceptedFiles[0].size,
              type: acceptedFiles[0].type.split('/')[1],
              entityType: entity,
            },
          },
        },
        {
          onSuccess: doSuccess,
          onError: doError,
        }
      );
    }
  };

  useEffect(() => {
    setLocalFile(file);
  }, [file]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    onDropRejected: (errors) => onError(errors[0].errors[0].code),
    onDrop,
    multiple: false,
    accept: 'image/jpeg, image/png',
    ...other,
  });

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        my: 2,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
      }}
    >
      {fileRejections.map(({ file: f, errors }) => {
        const { path, size }: CustomFile = f;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" color="black">
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography
                key={e.code}
                variant="caption"
                component="p"
                color="black"
              >
                - {UPLOAD_ERRORS[e.code]}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return (
    <>
      <RootStyle
        sx={{
          ...sx,
          margin: noMargin ? 0 : '',
          borderRadius: circle ? '50%' : '20%',
        }}
      >
        <DropZoneStyle
          {...getRootProps()}
          sx={{
            borderRadius: circle ? '50%' : '20%',
            ...(isDragActive && { opacity: 0.72 }),
            ...((isDragReject || error) && {
              color: 'error.main',
              borderColor: 'error.light',
              bgcolor: 'error.lighter',
              position: 'relative',
            }),
          }}
        >
          <input id={id} {...getInputProps()} />

          {localFile && (
            <Box
              alt="پیش‌نمایش"
              component={type}
              src={isString(localFile) ? localFile : localFile.preview}
              sx={{ zIndex: 8, objectFit: 'cover' }}
            />
          )}

          {isLoading && (
            <Box
              position="absolute"
              sx={{
                zIndex: '10',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <PlaceholderStyle
            className="placeholder"
            sx={{
              ...(file && {
                opacity: 0,
                color: 'common.white',
                bgcolor: 'grey.900',
                '&:hover': { opacity: 0.72 },
              }),
            }}
          >
            <AddImageIcon />
            <Typography variant="caption">
              {imageCaption || 'بارگذاری تصویر'}
            </Typography>
          </PlaceholderStyle>
        </DropZoneStyle>
      </RootStyle>

      {caption}

      {showError && fileRejections.length > 0 && <ShowRejectionItems />}
    </>
  );
}
